import { useCallback, useState } from 'react';
import debounce from 'lodash/debounce';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '../../../ui/feedback/Dialog';
import { WithHint } from '../../../ui/feedback/Tooltip';
import { Button } from '../../../ui/input/Button';
import { DataTestId } from '../../../ui/layout/DataTestIdWrapper';
import { Typography } from '../../../ui/typography/Typography';
import { useTranslation } from '../../../utils/useTranslation';
import { FOOTER_HEIGHT, SCROLL_OFFSET, SCROLL_TIMEOUT } from './constants';
import { ContentPromotionPostsDialogContent } from './parts/ContentPromotionPostsDialogContent/ContentPromotionPostsDialogContent';
import { makeDisabled, makeIdentifiers } from './utils';
import type { ContentPromotionPostsDialogProps } from './types';

export const ContentPromotionPostsDialog: React.FC<ContentPromotionPostsDialogProps> = ({
    posts,
    disabledPosts,
    onClose,
    onSave,
}) => {
    const t = useTranslation();
    const [disabled, setDisabled] = useState<Record<string, boolean>>(makeDisabled(disabledPosts));
    const disabledIds = makeIdentifiers(disabled);

    const onSelect = useCallback(
        (postId: string, value: boolean) => {
            if (value) {
                const newDisabledIds = disabledIds.filter((id) => id !== postId);
                setDisabled(makeDisabled(newDisabledIds));
            } else {
                setDisabled({ ...disabled, [postId]: true });
            }
        },
        [disabledIds, disabled],
    );

    const onScroll = debounce((event) => {
        const element = event.target;
        const isScrollToEnd =
            element.scrollHeight - element.scrollTop <= element.clientHeight + SCROLL_OFFSET;

        if (isScrollToEnd && !posts.isLoading && !posts.isEndReached) {
            posts.onLoadNext();
        }
    }, SCROLL_TIMEOUT);

    const onSavePosts = useCallback(() => {
        onSave(disabledIds);
    }, [disabledIds, onSave]);

    return (
        <Dialog onClose={onClose} dataTestId={DataTestId.CONTENT_PROMOTION_CHOOISING_POSTS_DIALOG}>
            <DialogTitle onClose={onClose} withBorderBottom={false} overflow="visible">
                <Typography variant="title3" weight="medium">
                    {t('Выбрать посты')}
                </Typography>
                <WithHint
                    ml={1}
                    message={t(
                        'Для продвижения будут выбраны все посты свежее 90 дней. Вы можете исключить отдельные посты, для этого снимите галочку в строке с нужным постом. Все новые посты будут добавлены с включенной галочкой для продвижения.Рекомендуем выбирать опцию "все посты", будут добавлены все посты свежее 90 дней или опцию "Посты свежее 30 дней".',
                    )}
                    iconColor="var(--color-secondary)"
                />
            </DialogTitle>
            <DialogContent px={0} py={0} onScroll={onScroll}>
                {posts.isLoading || posts.items.length > 0 ? (
                    <ContentPromotionPostsDialogContent
                        posts={posts}
                        disabled={disabled}
                        onSelect={onSelect}
                    />
                ) : (
                    <Typography variant="body" weight="medium" color="secondary" m="auto">
                        {t('Ничего не найдено')}
                    </Typography>
                )}
            </DialogContent>
            <DialogFooter minHeight={FOOTER_HEIGHT}>
                <Button width="100%" mt={0} size="l" onClick={onSavePosts}>
                    {t('Сохранить')}
                </Button>
            </DialogFooter>
        </Dialog>
    );
};
